// backoffice/src/pages/planning/DelegateLeaveFormModal.js

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, DatePicker, Select, Button, message, Spin } from 'antd';
import { useDispatch } from 'react-redux';
// Importer TOUS les thunks nécessaires
import { createDelegateLeave, updateDelegateLeave, fetchDelegateLeaves } from '../../redux/slices/planning/planningThunks';
import { getAllDelegatesList } from '../../services/delegateService';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const dateFormat = 'YYYY-MM-DD';

function DelegateLeaveFormModal({ visible, onCancel, leave }) {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [delegates, setDelegates] = useState([]);
  const [loadingDelegates, setLoadingDelegates] = useState(false);
  const [errorDelegates, setErrorDelegates] = useState(null); // Ajouter état erreur

  const isEditing = !!leave;

  // Charger les délégués (inchangé)
  useEffect(() => {
    if (visible) {
      setLoadingDelegates(true); setDelegates([]); setErrorDelegates(null);
      getAllDelegatesList()
        .then(response => { /* ... extraction ... */ setDelegates(Array.isArray(response?.data) ? response.data : (Array.isArray(response?.data?.data) ? response.data.data : [])); })
        .catch((err) => { message.error('Erreur chargement délégués.'); console.error(err); setErrorDelegates('Erreur chargement délégués.'); })
        .finally(() => setLoadingDelegates(false));
    }
  }, [visible]);

  // Pré-remplir (inchangé)
  useEffect(() => {
     if (visible) {
        if (isEditing && leave) {
            form.setFieldsValue({
                delegate_id: leave.delegate?.id,
                dates: [
                    leave.start_date ? dayjs(leave.start_date, dateFormat) : null,
                    leave.end_date ? dayjs(leave.end_date, dateFormat) : null
                ].filter(d => d && d.isValid()),
                reason: leave.reason,
            });
        } else {
          form.resetFields();
        }
     }
  }, [leave, form, isEditing, visible]);

  // Soumission formulaire
  const handleOk = () => {
    form.validateFields()
      .then((values) => {
        setLoading(true);
        const [startDate, endDate] = values.dates;
        const dataToSend = {
          delegate_id: values.delegate_id,
          start_date: startDate.format(dateFormat),
          end_date: endDate.format(dateFormat),
          reason: values.reason || null,
        };

        const action = isEditing
          ? dispatch(updateDelegateLeave({ id: leave.id, ...dataToSend }))
          : dispatch(createDelegateLeave(dataToSend));

        action.unwrap()
          .then(() => {
            message.success(`Congé ${isEditing ? 'mis à jour' : 'créé'} avec succès!`);
            onCancel();
            // --- RECHARGER LA LISTE COMPLÈTE ---
            console.log("Dispatching fetchDelegateLeaves after CUD operation");
            dispatch(fetchDelegateLeaves()); // <-- AJOUT IMPORTANT
            // ------------------------------------
          })
          .catch((err) => { console.error('Erreur formulaire congé:', err); })
          .finally(() => { setLoading(false); });
      })
      .catch((info) => { console.log('Erreur validation formulaire congé:', info); });
  };

  return (
    <Modal
      title={isEditing ? 'Modifier le Congé' : 'Ajouter un Congé'}
      visible={visible}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="back" onClick={onCancel}> Annuler </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleOk}>
           {isEditing ? 'Mettre à jour' : 'Créer'}
        </Button>,
       ]}
    >
       <Spin spinning={loadingDelegates} tip="Chargement des délégués...">
        <Form form={form} layout="vertical" name="leave_form" initialValues={{ reason: '' }}>
          <Form.Item
            name="delegate_id"
            label="Délégué"
            rules={[{ required: true, message: 'Veuillez sélectionner un délégué !' }]}
          >
            <Select
              placeholder="Sélectionner un délégué"
              disabled={loadingDelegates}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              notFoundContent={loadingDelegates ? null : "Aucun délégué"}
            >
              {delegates.map(d => (
                <Option key={d.id} value={d.id}>{`${d.firstname || ''} ${d.lastname || ''}`.trim() || `ID: ${d.id}`}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
              name="dates"
              label="Période du Congé"
              rules={[{ required: true, message: 'Veuillez sélectionner une période !' }]}
          >
               <RangePicker
                  format={dateFormat}
                  style={{ width: '100%' }}
                  disabledDate={(current) => {
                    const dates = form.getFieldValue('dates');
                    if (dates && dates[0] && !dates[1]) { return current && current < dayjs(dates[0]).startOf('day'); }
                    return false;
                  }}
               />
          </Form.Item>

           <Form.Item name="reason" label="Raison (Optionnel)">
            <TextArea rows={3} placeholder="Ex: Congé annuel, Maladie, Formation..." />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
}

export default DelegateLeaveFormModal;